import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnlyOfficeDocEditorComponent } from '../only-office-doc-editor/only-office-doc-editor.component';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { environment } from '../../../../environments/environment.dev';
import { JWTService } from '../../../contract-management/services/jwt.service';
import { FILE_TYPE, ONLY_OFFICE_EDITOR_TYPE } from '../../../@core/models/enums';
import { IAttachment, IUser } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';

@Component({
    selector: 'app-document-preview',
    imports: [CommonModule, OnlyOfficeDocEditorComponent],
    standalone: true,
    templateUrl: './document-preview.component.html',
    styleUrls: []
})
export class DocumentPreviewComponent implements OnInit {
    @Input() documentId: string = '';
    /** When true, heavy previews (like OnlyOffice) should temporarily reduce work during container resizing. */
    @Input() isResizing: boolean = false;
    private attachment: IAttachment = {} as IAttachment;
    @Input() set file(value: IAttachment) {
        this.attachment = value;
        this.setupConfig();
    }

    config!: IConfig;
    currentUser: any;
    private jwtService = new JWTService();
    private _uniqueKey: string = '';

    constructor(private authService: AuthenticationService) {
        this.authService.currentUser.subscribe(res => {
            this.currentUser = res as IUser
        });
    }

    ngOnInit() {
        this.generateEditorId();
    }

    private generateEditorId(): void {
        if (this.documentId && !this._uniqueKey) {
            this._uniqueKey = this.documentId + '-' + Math.random().toString(36).substring(2, 15);
        }
    }

    get uniqueKey(): string {
        if (!this._uniqueKey && this.documentId) {
            this.generateEditorId();
        }
        return this._uniqueKey;
    }

    setupConfig() {
        if (!this.attachment || !this.attachment.fullUrl) {
            return;
        }

        const docFormat = this.getDocumentFormat(this.attachment);
        const editorType = this.getEditorTypeForFile(docFormat);
        const tokenConfig: Partial<IConfig> = {
            type: 'embedded',
            document: {
                fileType: docFormat,
                key: this.uniqueKey,
                title: this.attachment?.filename ?? 'Document Preview',
                url: this.attachment?.fullUrl ?? '',
                permissions: {
                    chat: false,
                    comment: false,
                    download: true,
                    edit: false,
                    fillForms: false,
                    modifyContentControl: false,
                    modifyFilter: false,
                    print: false,
                    protect: true,
                    review: false,
                    reviewGroups: [],
                    userInfoGroups: [],
                },
            },
            documentType: editorType,
            editorConfig: {
                user: {
                    group: this.documentId,
                    id: this.currentUser?.email,
                    image: this.currentUser?.profilePicture?.url ? `${environment.s3Url}/${this.currentUser.profilePicture.url}?timestamp=${new Date().getTime()}` : '',
                    name: this.currentUser?.firstName + ' ' + this.currentUser?.lastName
                },
                embedded: {
                    // toolbarDocked: "bottom",
                },
                customization: {
                    toolbarHideFileName: true,
                    hideRightMenu: true,
                    compactToolbar: true,
                    compactHeader: true,
                    comments: false,
                    forcesave: true,
                    uiTheme: "theme-gray",
                    logo: {
                        image: `${environment.s3Url}/inndoc-ai/assets/images/3.png`,
                    },
                    zoom: -2, // Fit to width
                },
            },
            height: "100%",
            width: "100%",
        }
        const token = this.jwtService.signToken(tokenConfig, environment.jwtSecret);
        this.config = {
            ...tokenConfig,
            token: token
        };
    }

    getDocumentFormat(attachment: IAttachment): FILE_TYPE {
        const mimeType = attachment?.type?.toLowerCase() || '';
        const filename = attachment?.filename?.toLowerCase() || '';
        const url = attachment?.url?.toLowerCase() || '';

        const source = filename || url;
        const parts = source.split('.');
        const extension = parts.length > 1 ? parts.pop() || '' : '';

        switch (mimeType) {
            case 'application/pdf':
                return FILE_TYPE.PDF;
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case 'application/msword':
                return FILE_TYPE.WORD;
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return FILE_TYPE.EXCEL
            case 'application/vnd.ms-powerpoint':
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
                return FILE_TYPE.POWERPOINT
            case 'text/csv':
            case 'application/csv':
                return FILE_TYPE.CSV;
        }

        switch (extension) {
            case 'pdf':
                return FILE_TYPE.PDF;
            case 'doc':
            case 'docx':
                return FILE_TYPE.WORD;
            case 'xls':
            case 'xlsx':
                return FILE_TYPE.EXCEL;
            case 'ppt':
            case 'pptx':
                return FILE_TYPE.POWERPOINT;
            case 'csv':
                return FILE_TYPE.CSV;
            default:
                return FILE_TYPE.WORD
        }
    }

    getEditorTypeForFile(fileType: FILE_TYPE): ONLY_OFFICE_EDITOR_TYPE {
        switch (fileType) {
            case FILE_TYPE.WORD:
                return ONLY_OFFICE_EDITOR_TYPE.WORD
            case FILE_TYPE.PDF:
                return ONLY_OFFICE_EDITOR_TYPE.PDF
            case FILE_TYPE.EXCEL:
            case FILE_TYPE.CSV:
                return ONLY_OFFICE_EDITOR_TYPE.EXCEL
            case FILE_TYPE.POWERPOINT:
                return ONLY_OFFICE_EDITOR_TYPE.POWERPOINT
            default:
                return ONLY_OFFICE_EDITOR_TYPE.WORD
        }
    }


}
